// The History tab's pure parts (Phase 4, item 6): deals split open and past, the product orgs
// with their trend, and one timeline of every touch. Pure, so `node --test` covers it.
import type { AccountOrg, BookAccount, GongCall, Opportunity } from '../api/types.ts';
import type { OutreachPerson } from './outreachPeople.ts';
import { openOpportunities } from './panelFacts.ts';

/** Open deals (closed false or unknown) by close date, soonest first; an undated one goes last. */
export function openDeals(row: BookAccount): Opportunity[] {
  return openOpportunities(row)
    .sort((first, second) => (first.close_date || '9999').localeCompare(second.close_date || '9999'));
}

/** Closed deals, won and lost, by close date, newest first. */
export function pastDeals(row: BookAccount): Opportunity[] {
  return row.opportunities
    .filter(deal => deal.closed === true)
    .sort((first, second) => (second.close_date || '').localeCompare(first.close_date || ''));
}

/** "Closed won", "Closed lost", "Disqualified": the outcome a past deal reads as. */
export function dealOutcome(deal: Opportunity): string {
  if (deal.won) return 'Closed won';
  if (deal.stage && !/closed/i.test(deal.stage)) return deal.stage;
  return 'Closed lost';
}

/** An org's name without the plan Salesforce appends ("ops@x.com - pro" → "ops@x.com"). */
export function orgDisplayName(name: string): string {
  if (!name) return '—';
  return name.replace(/\s*-\s*(free|pro|startup|scale|enterprise)\s*$/i, '');
}

export interface OrgTrend {
  org: AccountOrg;
  name: string;
  // People on this account whose product facts name the org; null when none match.
  users: number | null;
  // Prior 30 days, then the last 30: the two points the trend line joins.
  points: [number, number];
  changePct: number | null;
}

/** Every product org on the account, busiest first, with the change against the 30 days before. */
export function orgTrends(row: BookAccount, people: OutreachPerson[]): OrgTrend[] {
  return row.orgs
    .map(org => {
      const name = orgDisplayName(org.name);
      const users = people.filter(person => {
        const orgName = person.playground?.org_name;
        return orgName === org.name || orgName === name;
      }).length;
      const prior = org.credits_prior_30 ?? 0;
      const recent = org.credits_30 ?? 0;
      const changePct = prior > 0 ? ((recent - prior) / prior) * 100 : null;
      return { org, name, users: users || null, points: [prior, recent] as [number, number], changePct };
    })
    .sort((first, second) => second.points[1] - first.points[1]);
}

export type TouchKind = 'email' | 'call' | 'meeting' | 'linkedin' | 'other';
export const TOUCH_FILTERS: { kind: TouchKind | 'all'; label: string }[] = [
  { kind: 'all', label: 'All' },
  { kind: 'email', label: 'Emails' },
  { kind: 'call', label: 'Calls' },
  { kind: 'meeting', label: 'Meetings' },
  { kind: 'linkedin', label: 'LinkedIn' },
];

export interface Touch {
  kind: TouchKind;
  day: string;
  // The buyer's side: the contact, or the call's attendees.
  who: string;
  // Our side: who sent it or hosted it.
  by: string | null;
  // 'out' for something we sent, 'in' for something they sent; null for a meeting or a row the
  // mirror could not resolve (book.py emits 'sent', 'received', 'unresolved').
  direction: 'out' | 'in' | null;
  detail: string | null;
  gong: GongCall | null;
}

function touchKind(channel: string): TouchKind {
  if (channel === 'email' || channel === 'call' || channel === 'linkedin') return channel;
  return 'other';
}

/** Mirror touches and Gong meetings as one list, newest first. */
export function touchTimeline(row: BookAccount): Touch[] {
  const touches: Touch[] = row.timeline.map(entry => ({
    kind: touchKind(entry.channel),
    day: entry.day,
    who: entry.who || entry.channel,
    by: entry.author,
    direction: entry.direction === 'sent' ? 'out' : entry.direction === 'received' ? 'in' : null,
    detail: entry.campaign || entry.call_outcome || null,
    gong: null,
  }));

  // A Gong call is a meeting held, with the buyer's addresses as the attendees.
  row.gong_calls.forEach(call => {
    if (!call.day) return;
    touches.push({
      kind: 'meeting', day: call.day, who: call.them.join(', ') || 'Attendees not recorded',
      by: call.host, direction: null, detail: call.title, gong: call,
    });
  });
  return touches.sort((first, second) => second.day.localeCompare(first.day));
}

/** What a touch was, when it has no campaign or call outcome: only an inbound email or LinkedIn
 * message is a Reply. */
export function touchLabel(touch: Touch): string {
  if (touch.detail) return touch.detail;
  if (touch.direction === 'in') return touch.kind === 'call' ? 'Inbound call' : touch.kind === 'meeting' ? '' : 'Reply';
  if (touch.direction === 'out') return touch.kind === 'call' ? 'Call' : 'Sent';
  return '';
}

/** The touches one filter chip shows. */
export function filterTouches(touches: Touch[], kind: TouchKind | 'all'): Touch[] {
  return kind === 'all' ? touches : touches.filter(touch => touch.kind === kind);
}

export interface HistoryCounts {
  open: number;
  past: number;
  orgs: number;
  touches: number;
  journeys: number;
}

/** The sections with nothing in them, said once as one line, so a cold account reads as one
 * sentence rather than five empty headings. Null when every section has something. */
export function emptyHistoryLine(counts: HistoryCounts): string | null {
  const empty: string[] = [];
  if (!counts.open) empty.push('open deals');
  if (!counts.past) empty.push('past deals');
  if (!counts.orgs) empty.push('Cartesia orgs');
  if (!counts.touches) empty.push('touches');
  if (!counts.journeys) empty.push('customer journeys');
  return empty.length ? `None on record: ${empty.join(' · ')}.` : null;
}
